<template>
    <div class="build">
        <div class="build__header">
            <h2 class="build__title">Build Program</h2>
            <p class="build__subtitle">
                Capture preferences, review the syllables this learner can say, then generate word ideas for targets.
            </p>
        </div>

        <!-- Section 1: Preferences -->
        <section class="build-card" :class="{ 'build-card--done': hasPreferences }">
            <header class="build-card__header">
                <div class="build-card__step">1</div>
                <div class="build-card__heading">
                    <h3 class="build-card__title">Preferences</h3>
                    <p class="build-card__sub">What does this learner enjoy? This shapes the AI's word suggestions.</p>
                </div>
                <div v-if="hasPreferences" class="build-card__status">
                    <span class="material-symbols-outlined">check_circle</span>
                    <span>Saved</span>
                </div>
            </header>

            <div class="build-card__body">
                <div class="pref-grid">
                    <label class="pref-field">
                        <span class="pref-field__label">
                            <span class="material-symbols-outlined">sports_soccer</span>
                            Activities
                        </span>
                        <textarea
                            v-model="preferences.activities"
                            placeholder="e.g., swimming, playing with trains, music time"
                            rows="3"
                        ></textarea>
                    </label>
                    <label class="pref-field">
                        <span class="pref-field__label">
                            <span class="material-symbols-outlined">toys</span>
                            Items
                        </span>
                        <textarea
                            v-model="preferences.items"
                            placeholder="e.g., teddy bear, toy car, iPad, snack"
                            rows="3"
                        ></textarea>
                    </label>
                    <label class="pref-field">
                        <span class="pref-field__label">
                            <span class="material-symbols-outlined">group</span>
                            People & Places
                        </span>
                        <textarea
                            v-model="preferences.places"
                            placeholder="e.g., mom, grandma, park, school"
                            rows="3"
                        ></textarea>
                    </label>
                </div>
                <div class="build-card__actions">
                    <button
                        type="button"
                        class="btn btn--primary"
                        :disabled="prefSaving"
                        @click="savePreferences"
                    >
                        <span v-if="prefSaving">Saving...</span>
                        <span v-else>Save Preferences</span>
                    </button>
                </div>
            </div>
        </section>

        <!-- Section 2: Syllable Tracker -->
        <section class="build-card" :class="{ 'build-card--done': knownSyllables.length > 0 }">
            <header class="build-card__header">
                <div class="build-card__step">2</div>
                <div class="build-card__heading">
                    <h3 class="build-card__title">Syllable Tracker</h3>
                    <p class="build-card__sub">
                        <span v-if="autoFilledCount > 0">
                            {{ autoFilledCount }} syllable{{ autoFilledCount === 1 ? '' : 's' }} auto-filled from the assessment.
                            Add any others you've observed informally.
                        </span>
                        <span v-else>
                            Mark any syllables you've heard this learner produce.
                        </span>
                    </p>
                </div>
                <div v-if="knownSyllables.length > 0" class="build-card__status">
                    <span class="material-symbols-outlined">check_circle</span>
                    <span>{{ knownSyllables.length }} known</span>
                </div>
            </header>

            <div class="build-card__body">
                <!-- Quick view summary -->
                <div v-if="knownSyllables.length > 0" class="syllable-chips">
                    <span
                        v-for="s in knownSyllables.slice(0, 20)"
                        :key="s"
                        class="syllable-chip"
                    >{{ s }}</span>
                    <span v-if="knownSyllables.length > 20" class="syllable-chip syllable-chip--more">
                        +{{ knownSyllables.length - 20 }} more
                    </span>
                </div>

                <!-- Toggle expanded matrix -->
                <button
                    type="button"
                    class="btn btn--secondary btn--wide"
                    @click="showMatrix = !showMatrix"
                >
                    <span class="material-symbols-outlined">{{ showMatrix ? 'expand_less' : 'expand_more' }}</span>
                    {{ showMatrix ? 'Hide tracker' : (knownSyllables.length > 0 ? 'Edit tracker' : 'Open tracker') }}
                </button>

                <div v-if="showMatrix" class="syllable-matrix-wrapper">
                    <SyllableTracker
                        :assessmentId="assessmentId"
                        :organizationId="organizationId"
                        :learnerId="learnerId"
                        :canConductAssessment="canConductAssessment"
                    />
                </div>
            </div>
        </section>

        <!-- Section 3: Word ideas -->
        <section class="build-card" :class="{ 'build-card--done': note.length > 0 }">
            <header class="build-card__header">
                <div class="build-card__step">3</div>
                <div class="build-card__heading">
                    <h3 class="build-card__title">Word Ideas for Verbal Targets</h3>
                    <p class="build-card__sub">
                        Generate words using preferences + known syllables, or add your own.
                    </p>
                </div>
                <div v-if="note.length > 0" class="build-card__status">
                    <span class="material-symbols-outlined">check_circle</span>
                    <span>{{ note.length }} word{{ note.length === 1 ? '' : 's' }}</span>
                </div>
            </header>

            <div class="build-card__body">
                <!-- Constraint summary -->
                <div class="constraint-bar" v-if="hasSCV">
                    <div class="constraint-item">
                        <span class="constraint-item__label">Syllable range</span>
                        <span class="constraint-item__value">{{ syllableRange }}</span>
                    </div>
                    <div class="constraint-item">
                        <span class="constraint-item__label">Max syllables</span>
                        <span class="constraint-item__value">{{ maxMetrics.syllable }}</span>
                    </div>
                    <div class="constraint-item">
                        <span class="constraint-item__label">Max consonants</span>
                        <span class="constraint-item__value">{{ maxMetrics.consonant }}</span>
                    </div>
                    <div class="constraint-item">
                        <span class="constraint-item__label">Max vowels</span>
                        <span class="constraint-item__value">{{ maxMetrics.vowel }}</span>
                    </div>
                </div>

                <!-- Generate controls -->
                <div class="ai-controls">
                    <button
                        type="button"
                        class="btn btn--primary btn--generate"
                        :disabled="generating || !hasSCV"
                        @click="generateWords"
                    >
                        <span class="material-symbols-outlined">auto_awesome</span>
                        <span v-if="generating">Generating ideas...</span>
                        <span v-else-if="suggestions.length > 0">Regenerate ideas</span>
                        <span v-else>Generate word ideas</span>
                    </button>
                    <button
                        type="button"
                        class="btn btn--secondary"
                        @click="showCustomForm = !showCustomForm"
                    >
                        <span class="material-symbols-outlined">add</span>
                        Add custom word
                    </button>
                </div>

                <!-- Custom add form -->
                <div v-if="showCustomForm" class="custom-form">
                    <div class="custom-form__grid">
                        <label>
                            <span>Word</span>
                            <input type="text" v-model.trim="custom.word" placeholder="e.g., teddy" />
                        </label>
                        <label>
                            <span>Phonetic (IPA)</span>
                            <input type="text" v-model.trim="custom.ipa" placeholder="/ˈted.i/" />
                        </label>
                        <label>
                            <span>Consonants</span>
                            <input type="text" v-model.trim="custom.consonants" placeholder="t, d" />
                        </label>
                        <label>
                            <span>Vowels</span>
                            <input type="text" v-model.trim="custom.vowels" placeholder="ɛ, i" />
                        </label>
                    </div>
                    <div class="custom-form__actions">
                        <button type="button" class="btn btn--secondary" @click="showCustomForm = false">Cancel</button>
                        <button type="button" class="btn btn--primary" :disabled="!custom.word" @click="addCustomWord">Add</button>
                    </div>
                </div>

                <!-- Suggestions from AI -->
                <div v-if="suggestions.length > 0" class="suggestions">
                    <div class="suggestions__head">
                        <h4>AI suggestions ({{ suggestions.length }})</h4>
                        <button
                            type="button"
                            class="btn-link"
                            @click="addAllSuggestions"
                            :disabled="allSuggestionsAdded"
                        >
                            {{ allSuggestionsAdded ? 'All added' : 'Add all' }}
                        </button>
                    </div>
                    <div class="suggestion-grid">
                        <div
                            v-for="(sug, idx) in suggestions"
                            :key="idx"
                            class="suggestion"
                            :class="{ 'suggestion--added': isInNote(sug) }"
                        >
                            <div class="suggestion__head">
                                <div class="suggestion__word">{{ sug.word }}</div>
                                <div class="suggestion__ipa">{{ sug.ipa }}</div>
                            </div>
                            <div class="suggestion__meta">
                                <span>C: {{ Array.isArray(sug.consonants) ? sug.consonants.join(', ') : sug.consonants }}</span>
                                <span>V: {{ Array.isArray(sug.vowels) ? sug.vowels.join(', ') : sug.vowels }}</span>
                            </div>
                            <div v-if="sug.source" class="suggestion__tags">
                                <span
                                    v-for="tag in asArray(sug.source)"
                                    :key="tag"
                                    class="source-tag"
                                    :class="`source-tag--${tag}`"
                                    :title="sourceTooltip(tag)"
                                >{{ sourceLabel(tag) }}</span>
                            </div>
                            <button
                                type="button"
                                class="suggestion__add"
                                :disabled="isInNote(sug)"
                                @click="addSuggestion(sug)"
                            >
                                <span class="material-symbols-outlined">{{ isInNote(sug) ? 'check' : 'add' }}</span>
                                {{ isInNote(sug) ? 'Added' : 'Add' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Saved words list -->
                <div class="saved-words" v-if="note.length > 0">
                    <div class="saved-words__head">
                        <h4>Your word ideas ({{ note.length }})</h4>
                        <button
                            type="button"
                            class="btn btn--primary btn--small"
                            :disabled="!hasNoteChanges || noteSaving"
                            @click="saveNote"
                        >
                            <span v-if="noteSaving">Saving...</span>
                            <span v-else>Save</span>
                        </button>
                    </div>
                    <div class="saved-grid">
                        <div
                            v-for="(item, idx) in note"
                            :key="idx"
                            class="saved-word"
                        >
                            <div class="saved-word__main">
                                <div class="saved-word__word">{{ item.word }}</div>
                                <div class="saved-word__ipa">{{ item.ipa }}</div>
                            </div>
                            <div class="saved-word__meta">
                                <span>C: {{ formatCV(item.consonants) }}</span>
                                <span>V: {{ formatCV(item.vowels) }}</span>
                            </div>
                            <button
                                type="button"
                                class="saved-word__remove"
                                @click="removeNote(idx)"
                                title="Remove"
                            >
                                <span class="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="note.length === 0 && suggestions.length === 0" class="empty-ideas">
                    <span class="material-symbols-outlined">auto_awesome</span>
                    <p>Click <strong>Generate word ideas</strong> to get started. The AI uses your preferences and syllable tracker data to suggest personalized words.</p>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import { showDialog, showToast } from '@/utils/swal';
import SyllableTracker from '@/components/assesments/workpacket/SyllableTracker.vue';

const props = defineProps({
    organizationId: Number,
    learnerId: Number,
    assessmentId: Number,
    assessment: Object,
    canConductAssessment: Boolean,
});

// Preferences state
const preferences = ref({ activities: '', items: '', places: '' });
const originalPrefs = ref({ activities: '', items: '', places: '' });
const prefSaving = ref(false);

// Syllable tracker state
const showMatrix = ref(false);
const knownSyllables = ref([]);
const autoFilledCount = ref(0);

// AI + word ideas state
const suggestions = ref([]);
const generating = ref(false);
const note = ref([]);
const originalNote = ref([]);
const noteSaving = ref(false);
const showCustomForm = ref(false);
const custom = ref({ word: '', ipa: '', consonants: '', vowels: '' });

// Computed SCV from assessment
const maxMetrics = computed(() => {
    const averages = props.assessment?.result?.scv_averages || {};
    const vals = Object.values(averages);
    if (!vals.length) return { syllable: 0, consonant: 0, vowel: 0 };
    const max = (key) => Math.max(0, ...vals.map(v => parseFloat(v?.[key]) || 0));
    return {
        syllable: max('syllable'),
        consonant: max('consonant'),
        vowel: max('vowel'),
    };
});

const hasSCV = computed(() =>
    maxMetrics.value.syllable > 0
    && maxMetrics.value.consonant > 0
    && maxMetrics.value.vowel > 0
);

const syllableRange = computed(() => {
    const raw = props.assessment?.result?.group_scores || {};
    const s = {};
    for (let i = 1; i <= 5; i++) {
        s[i] = raw[i]?.total > 0 ? (raw[i].score_one / raw[i].total) * 100 : 0;
    }
    if (s[1] >= 80 && s[2] >= 80 && s[3] >= 80 && s[4] < 80) return '2–3';
    if (s[1] >= 80 && s[2] >= 80 && s[3] >= 80 && s[4] >= 80 && s[5] < 80) return '3–4';
    if (s[1] >= 80 && s[2] >= 80 && s[3] >= 80 && s[4] >= 80 && s[5] >= 80) return '4–5';
    return '—';
});

const hasPreferences = computed(() =>
    [preferences.value.activities, preferences.value.items, preferences.value.places].some(v => v && v.trim())
);

const hasNoteChanges = computed(() =>
    JSON.stringify(note.value) !== JSON.stringify(originalNote.value)
);

const allSuggestionsAdded = computed(() =>
    suggestions.value.length > 0 && suggestions.value.every(s => isInNote(s))
);

// Vowel/consonant mappings from existing tracker — used to extract known_syllables
const vowels = [
    { symbol: 'i', short: 'ee' }, { symbol: 'ɪ', short: 'ih' },
    { symbol: 'e', short: 'ay' }, { symbol: 'ɛ', short: 'eh' },
    { symbol: 'æ', short: '(none)' }, { symbol: 'a', short: 'ah' },
    { symbol: 'ʌ or ə', short: 'uh' }, { symbol: 'ʊ', short: '(none)' },
    { symbol: 'ɔ', short: 'aw' }, { symbol: 'o', short: 'oh' },
    { symbol: 'u', short: 'oo' }, { symbol: 'au', short: 'ow' },
    { symbol: 'aɪ', short: 'ai' }, { symbol: 'oɪ', short: 'oi' },
];

const consonantRows = [
    { label: 'w or wh' }, { label: 'h' }, { label: 'p or b' }, { label: 't or d' },
    { label: 'k or g' }, { label: 'm' }, { label: 'n' }, { label: 'ng or ŋ' },
    { label: 'f or v' }, { label: 'l' }, { label: 'y or j' },
];

const extractKnownSyllables = (workpacket) => {
    const known = new Set();
    let autoCount = 0;

    if (workpacket?.syllableTracker) {
        Object.entries(workpacket.syllableTracker).forEach(([combo, score]) => {
            if (score === 1) {
                known.add(combo);
                autoCount++;
            }
        });
    }

    if (workpacket?.manualSyllableTracker) {
        Object.entries(workpacket.manualSyllableTracker).forEach(([indexNo, jsonStr]) => {
            try {
                const parsed = JSON.parse(jsonStr);
                const idx = parseInt(indexNo) - 1;
                const cIndex = Math.floor(idx / vowels.length);
                const vIndex = idx % vowels.length;
                if (cIndex >= 0 && cIndex < consonantRows.length && vIndex >= 0 && vIndex < vowels.length) {
                    const cons = consonantRows[cIndex].label.split(/or/i).map(s => s.trim().toLowerCase());
                    const vShort = vowels[vIndex].short;
                    const firstC = cons.find(c => parsed[c] == '1');
                    if (firstC && parsed[vShort] == '1') {
                        known.add(`${firstC}${vShort}`);
                    }
                }
            } catch (e) { /* skip */ }
        });
    }

    if (workpacket?.manualSyllableTrackerVowel) {
        Object.entries(workpacket.manualSyllableTrackerVowel).forEach(([indexNo, jsonStr]) => {
            try {
                const parsed = JSON.parse(jsonStr);
                const vIndex = parseInt(indexNo) - 1;
                if (vIndex >= 0 && vIndex < vowels.length) {
                    const vShort = vowels[vIndex].short;
                    if (parsed[vShort] == '1' && vShort !== '(none)') {
                        known.add(vShort);
                    }
                }
            } catch (e) { /* skip */ }
        });
    }

    autoFilledCount.value = autoCount;
    return [...known];
};

const loadWorkpacket = async () => {
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/workpacket`
        );
        knownSyllables.value = extractKnownSyllables(data?.data);
    } catch (error) {
        handleError(error);
    }
};

const loadNotes = async () => {
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}`
        );
        const noteData = data?.data?.note;
        if (noteData?.preferences_list) {
            try {
                const parsed = JSON.parse(noteData.preferences_list);
                preferences.value = {
                    activities: parsed.activities || '',
                    items: parsed.items || '',
                    places: parsed.places || '',
                };
                originalPrefs.value = { ...preferences.value };
            } catch (e) { /* keep defaults */ }
        }
        if (noteData?.mini_program_plan) {
            try {
                note.value = JSON.parse(noteData.mini_program_plan) || [];
                originalNote.value = JSON.parse(JSON.stringify(note.value));
            } catch (e) { /* keep defaults */ }
        }
    } catch (error) {
        handleError(error);
    }
};

const savePreferences = async () => {
    if (!props.canConductAssessment) {
        showDialog({ icon: 'warning', html: "You don't have permission to perform this action." });
        return;
    }
    prefSaving.value = true;
    try {
        await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/notes`, {
            preferences_list: JSON.stringify(preferences.value),
        });
        originalPrefs.value = { ...preferences.value };
        showToast({ icon: 'success', text: 'Preferences saved' });
    } catch (error) {
        handleError(error);
    } finally {
        prefSaving.value = false;
    }
};

const generateWords = async () => {
    if (!hasSCV.value) return;
    if (!aiFeaturesAllowed()) return;
    generating.value = true;
    try {
        await loadWorkpacket();
        const { data } = await api.post('/open-ai/ipa/generate', {
            syllable: maxMetrics.value.syllable,
            consonants: maxMetrics.value.consonant,
            vowel: maxMetrics.value.vowel,
            activities: preferences.value.activities,
            items: preferences.value.items,
            people_places: preferences.value.places,
            known_syllables: knownSyllables.value,
        });
        suggestions.value = data?.results || [];
        if (suggestions.value.length === 0) {
            showToast({ icon: 'info', text: 'No suggestions returned — try different preferences' });
        }
    } catch (error) {
        handleError(error);
    } finally {
        generating.value = false;
    }
};

const addSuggestion = (sug) => {
    if (isInNote(sug)) return;
    note.value = [...note.value, {
        word: sug.word,
        ipa: sug.ipa,
        consonants: sug.consonants,
        vowels: sug.vowels,
    }];
};

const addAllSuggestions = () => {
    const toAdd = suggestions.value.filter(s => !isInNote(s));
    note.value = [
        ...note.value,
        ...toAdd.map(s => ({
            word: s.word,
            ipa: s.ipa,
            consonants: s.consonants,
            vowels: s.vowels,
        })),
    ];
};

const isInNote = (sug) => note.value.some(n => n.word?.toLowerCase() === sug.word?.toLowerCase());

const addCustomWord = () => {
    if (!custom.value.word) return;
    const splitList = (v) => v ? v.split(',').map(s => s.trim()).filter(Boolean) : [];
    note.value = [...note.value, {
        word: custom.value.word,
        ipa: custom.value.ipa || '',
        consonants: splitList(custom.value.consonants),
        vowels: splitList(custom.value.vowels),
    }];
    custom.value = { word: '', ipa: '', consonants: '', vowels: '' };
    showCustomForm.value = false;
};

const removeNote = (idx) => {
    note.value = note.value.filter((_, i) => i !== idx);
};

const saveNote = async () => {
    if (!props.canConductAssessment) {
        showDialog({ icon: 'warning', html: "You don't have permission to perform this action." });
        return;
    }
    noteSaving.value = true;
    try {
        await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/notes`, {
            mini_program_plan: JSON.stringify(note.value),
        });
        originalNote.value = JSON.parse(JSON.stringify(note.value));
        showToast({ icon: 'success', text: 'Word ideas saved' });
    } catch (error) {
        handleError(error);
    } finally {
        noteSaving.value = false;
    }
};

// Helpers
const asArray = (v) => Array.isArray(v) ? v : [v];
const formatCV = (v) => Array.isArray(v) ? v.join(', ') : (v || '');
const sourceLabel = (source) => ({
    syllable_tracker: 'Syllable Tracker',
    preferences: 'Preferences',
    scv_based: 'SCV-based',
})[source] || source;
const sourceTooltip = (source) => ({
    syllable_tracker: 'Uses only sounds the learner has demonstrated',
    preferences: 'Related to listed preferences',
    scv_based: 'Matches syllable complexity level',
})[source] || '';

onMounted(async () => {
    await loadNotes();
    await loadWorkpacket();
});
</script>

<style scoped>
.build {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.build__header {
    margin-bottom: 4px;
}

.build__title {
    font-size: 24px;
    font-weight: 700;
    color: #222;
    margin: 0;
}

.build__subtitle {
    color: #666;
    margin: 4px 0 0;
    font-size: 15px;
}

/* Card shared */
.build-card {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
    overflow: hidden;
    transition: border-color 0.2s;
    border: 1px solid transparent;
}

.build-card--done {
    border-color: rgba(54, 194, 122, 0.3);
}

.build-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 24px;
    border-bottom: 1px solid #f2f0ff;
}

.build-card__step {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #725CFF;
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.build-card--done .build-card__step {
    background: #36C27A;
}

.build-card__heading {
    flex: 1;
    min-width: 0;
}

.build-card__title {
    font-size: 17px;
    font-weight: 600;
    color: #222;
    margin: 0;
}

.build-card__sub {
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
    line-height: 1.4;
}

.build-card__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #36C27A;
    font-size: 13px;
    font-weight: 500;
}

.build-card__status .material-symbols-outlined {
    font-size: 18px;
}

.build-card__body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.build-card__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

/* Buttons */
.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: transform 0.1s, box-shadow 0.15s, background 0.15s;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn--primary {
    background: #725CFF;
    color: #fff;
    box-shadow: 0 2px 6px rgba(114, 92, 255, 0.25);
}

.btn--primary:not(:disabled):hover {
    background: #5d47e0;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(114, 92, 255, 0.35);
}

.btn--secondary {
    background: #f7f6ff;
    color: #725CFF;
    border: 1px solid #e4dfff;
}

.btn--secondary:not(:disabled):hover {
    background: #ece8ff;
}

.btn--wide {
    width: 100%;
}

.btn--small {
    padding: 6px 12px;
    font-size: 13px;
}

.btn--generate {
    font-weight: 600;
    padding: 12px 20px;
}

.btn .material-symbols-outlined {
    font-size: 18px;
}

.btn-link {
    background: none;
    border: none;
    color: #725CFF;
    font-weight: 500;
    cursor: pointer;
    font-size: 13px;
    padding: 4px 8px;
    border-radius: 4px;
}

.btn-link:hover:not(:disabled) {
    background: #f7f6ff;
}

.btn-link:disabled {
    color: #bbb;
    cursor: not-allowed;
}

/* Preferences grid */
.pref-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

@media (max-width: 900px) {
    .pref-grid {
        grid-template-columns: 1fr;
    }
}

.pref-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pref-field__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    font-size: 13px;
    color: #444;
}

.pref-field__label .material-symbols-outlined {
    font-size: 18px;
    color: #725CFF;
}

.pref-field textarea {
    border: 1px solid #e4dfff;
    border-radius: 8px;
    padding: 10px 12px;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.pref-field textarea:focus {
    outline: none;
    border-color: #725CFF;
    box-shadow: 0 0 0 3px rgba(114, 92, 255, 0.1);
}

/* Syllable chips */
.syllable-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.syllable-chip {
    background: #f0edff;
    color: #5d47e0;
    padding: 4px 10px;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 500;
}

.syllable-chip--more {
    background: #ededed;
    color: #666;
}

.syllable-matrix-wrapper {
    margin-top: 12px;
    background: #fafaff;
    border-radius: 8px;
    padding: 8px;
    overflow-x: auto;
}

/* Constraint bar */
.constraint-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    background: #f7f6ff;
    border-radius: 10px;
    padding: 14px;
}

@media (max-width: 640px) {
    .constraint-bar {
        grid-template-columns: repeat(2, 1fr);
    }
}

.constraint-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.constraint-item__label {
    font-size: 11px;
    color: #666;
}

.constraint-item__value {
    font-size: 18px;
    font-weight: 700;
    color: #725CFF;
}

/* AI controls */
.ai-controls {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

/* Custom form */
.custom-form {
    background: #f7f6ff;
    border-radius: 10px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.custom-form__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.custom-form__grid label {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.custom-form__grid label span {
    font-size: 12px;
    font-weight: 500;
    color: #444;
}

.custom-form__grid input {
    border: 1px solid #e4dfff;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 14px;
}

.custom-form__grid input:focus {
    outline: none;
    border-color: #725CFF;
}

.custom-form__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

/* Suggestions */
.suggestions {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.suggestions__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.suggestions__head h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: #444;
}

.suggestion-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
}

.suggestion {
    background: #fafaff;
    border: 1px solid #e4dfff;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: background 0.15s;
}

.suggestion--added {
    background: #f0f9f4;
    border-color: #cfeedb;
}

.suggestion__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.suggestion__word {
    font-weight: 600;
    color: #222;
    font-size: 15px;
}

.suggestion__ipa {
    color: #666;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.suggestion__meta {
    display: flex;
    gap: 10px;
    font-size: 11px;
    color: #666;
}

.suggestion__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.source-tag {
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;
    cursor: help;
}

.source-tag--syllable_tracker { background: #e8f9f0; color: #2e7d32; }
.source-tag--preferences { background: #e8f2ff; color: #1565c0; }
.source-tag--scv_based { background: #f3e8ff; color: #7b1fa2; }

.suggestion__add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: #725CFF;
    color: #fff;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 4px;
}

.suggestion__add:disabled {
    background: #36C27A;
    cursor: default;
}

.suggestion__add .material-symbols-outlined {
    font-size: 14px;
}

/* Saved words */
.saved-words {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.saved-words__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.saved-words__head h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: #444;
}

.saved-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 10px;
}

.saved-word {
    position: relative;
    background: #fff;
    border: 1px solid #ededed;
    border-radius: 10px;
    padding: 12px 32px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.saved-word__main {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
}

.saved-word__word {
    font-weight: 600;
    color: #222;
}

.saved-word__ipa {
    color: #666;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.saved-word__meta {
    display: flex;
    gap: 10px;
    font-size: 11px;
    color: #888;
}

.saved-word__remove {
    position: absolute;
    top: 8px;
    right: 8px;
    background: transparent;
    border: none;
    color: #bbb;
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.saved-word__remove:hover {
    background: #ffeded;
    color: #c44;
}

.saved-word__remove .material-symbols-outlined {
    font-size: 16px;
}

/* Empty state */
.empty-ideas {
    text-align: center;
    padding: 32px;
    background: #fafaff;
    border-radius: 10px;
    color: #666;
}

.empty-ideas .material-symbols-outlined {
    font-size: 40px;
    color: #725CFF;
    display: block;
    margin-bottom: 10px;
}

.empty-ideas p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
}
</style>
